import LoadingButton from '@mui/lab/LoadingButton';
import { Button, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import useMessageUsersScheduledSessions, { ScheduledTask } from '../../../../hooks/useMessageUsersScheduledSessions';
import useSextforceBigBrotherRestrictedWords from '../../../../hooks/useSextforceBigBrotherRestrictedWords';
import useSubscriber, { OnlyFansSubscriptionsUserType } from '../../../../hooks/useSubscriber';
import useSubscriberLists from '../../../../hooks/useSubscriberLists';
import useSubscriberMedia from '../../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditional from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { OnlyFansLists } from '../../../../types/onlyFansTypes';
import { d2f, isSextforceActive, stripHtml } from '../../../../utils/common';
import { getRestrictedWordsInMessage } from '../../../../utils/messageUsers';
import DialogAdaptive from '../../../common/DialogAdaptive';
import DialogContentAdaptive from '../../../common/DialogContentAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';
import MessageUsersForm, { MessageUsersAlbumContentTypes, MessageUsersContentType } from '../sendMessage/MessageUsersForm';

const momentPeriods: { [key: string]: string } = {
    minutes: 'Minutes',
    hours: 'Hours',
    days: 'Days',
};

type Props = {
    scheduledTask: ScheduledTask;
    onClose: () => void;
    open: boolean;
};

const MessageUsersScheduledTaskChangeSettingsDialog = (props: Props) => {
    const { scheduledTask, onClose, open } = props;
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const { data: subscriber } = useSubscriber();
    const { getThumbnail: getSubscriberThumbnail, getAudio: getSubscriberAudio } = useSubscriberMedia();
    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();

    const [messageUsersCurrentSession, setMessageUsersCurrentSession] = useState<any | null>(null);
    const [unitCost, setUnitCost] = useState<number>(0);

    const [name, setName] = useState<string>(scheduledTask.payload.name || '');
    const [scheduleMessage, setScheduleMessage] = useState<boolean>(true);
    const [scheduleMessageDate, setScheduleMessageDate] = useState<Date | null>(scheduledTask.scheduleDateTime);

    const [targetUsers, setTargetUsers] = useState<OnlyFansSubscriptionsUserType>(scheduledTask.payload.usersType);
    const [targetLists, setTargetLists] = useState<{ id: number | string | OnlyFansLists.Type; name: string }[]>(
        scheduledTask.payload.targetLists,
    );
    const [targetRecentSpendersDateFrom, setTargetRecentSpendersDateFrom] = useState<Date | null>(
        scheduledTask.payload.targetRecentSpendersDateFrom ? new Date(scheduledTask.payload.targetRecentSpendersDateFrom) : null,
    );
    const [targetRecentSpendersDateTo, setTargetRecentSpendersDateTo] = useState<Date | null>(
        scheduledTask.payload.targetRecentSpendersDateTo ? new Date(scheduledTask.payload.targetRecentSpendersDateTo) : null,
    );

    const [targetRecentFansDateFrom, setTargetRecentFansDateFrom] = useState<Date | null>(
        scheduledTask.payload.targetRecentFansDateFrom ? new Date(scheduledTask.payload.targetRecentFansDateFrom) : null,
    );
    const [targetRecentFansDateTo, setTargetRecentFansDateTo] = useState<Date | null>(
        scheduledTask.payload.targetRecentFansDateTo ? new Date(scheduledTask.payload.targetRecentFansDateTo) : null,
    );

    const [excludeCreators, setExcludeCreators] = useState<boolean>(scheduledTask.payload.exclude.excludeCreators);

    const [excludeUnreadChats, setExcludeUnreadChats] = useState<boolean>(scheduledTask.payload.exclude.excludeUnreadChats);

    const [excludeLimitMessage, setExcludeLimitMessage] = useState<boolean>(
        scheduledTask.payload.exclude.excludeLimitMessageCount ? true : false,
    );
    const [excludeLimitMessageCount, setExcludeLimitMessageCount] = useState<number>(
        scheduledTask.payload.exclude.excludeLimitMessageCount ?? 1,
    );

    const [excludeUsersList, setExcludeUsersList] = useState<boolean>(
        scheduledTask.payload.exclude.excludeLists?.length > 0 ? true : false,
    );
    const [excludeUsersLists, setExcludeUsersLists] = useState<any[]>(scheduledTask.payload.exclude.excludeLists);

    const [excludeUsersSpent, setExcludeUsersSpent] = useState<boolean>(scheduledTask.payload.exclude.excludeSpentAmount ? true : false);
    const [excludeSpentCondition, setExcludeSpentCondition] = useState<string>(scheduledTask.payload.exclude.excludeSpentCondition ?? 'eq');
    const [excludeSpentAmount, setExcludeSpentAmount] = useState<number>(scheduledTask.payload.exclude.excludeSpentAmount ?? 0);

    const [excludeUsersSpentPeriod, setExcludeUsersSpentPeriod] = useState<boolean>(
        scheduledTask.payload.exclude.excludeSpentPeriodAmount ? true : false,
    );
    const [excludeSpentPeriod, setExcludeSpentPeriod] = useState<string>(scheduledTask.payload.exclude.excludeSpentPeriod ?? 'months');
    const [excludeSpentPeriodAmount, setExcludeSpentPeriodAmount] = useState<number>(
        scheduledTask.payload.exclude.excludeSpentPeriodAmount ?? 0,
    );

    const [excludeUsersMessagedIn, setExcludeUsersMessagedIn] = useState<boolean>(
        scheduledTask.payload.exclude.excludeMessagedInPeriodAmount ? true : false,
    );
    const [excludeUsersMessagedInPeriod, setExcludeUsersMessagedInPeriod] = useState<string>(
        scheduledTask.payload.exclude.excludeMessagedInPeriod ?? 'hours',
    );
    const [excludeUsersMessagedInPeriodAmount, setExcludeUsersMessagedInPeriodAmount] = useState<number>(
        scheduledTask.payload.exclude.excludeMessagedInPeriodAmount ?? 0,
    );

    const [excludeFans, setExcludeFans] = useState<boolean>(scheduledTask.payload.exclude.excludeFans);

    // Content mode - legacy payloads without a type are manual
    const [messageType, setMessageType] = useState<MessageUsersContentType>(scheduledTask.payload.type || 'manual');
    const [sourceAlbumId, setSourceAlbumId] = useState<number>(scheduledTask.payload.sourceAlbumId ?? 0);
    const [albumContentTypes, setAlbumContentTypes] = useState<MessageUsersAlbumContentTypes>(
        scheduledTask.payload.type === 'album'
            ? {
                  image: !!scheduledTask.payload.postImage,
                  video: !!scheduledTask.payload.postVideo,
                  audio: !!scheduledTask.payload.postAudio,
                  gif: !!scheduledTask.payload.postGif,
              }
            : { image: true, video: true, audio: false, gif: false },
    );
    const [albumUseRandomCaption, setAlbumUseRandomCaption] = useState<boolean>(!!scheduledTask.payload.postCaption);
    const [captionCategory, setCaptionCategory] = useState<string>(scheduledTask.payload.captionCategory ?? '');
    const [templateIds, setTemplateIds] = useState<{ inventoryId: number; versionId: number }[]>(scheduledTask.payload.templateIds ?? []);
    const [templateTags, setTemplateTags] = useState<string[]>(scheduledTask.payload.templateTags ?? []);
    const [templateTagsPaidOnly, setTemplateTagsPaidOnly] = useState<boolean>(scheduledTask.payload.templateTagsPaidOnly ?? true);

    const [includeMedia, setIncludeMedia] = useState<boolean>(scheduledTask.payload.mediaFiles?.length > 0 ? true : false);
    const [medias, setMedias] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>([]);
    const [thumbnails, setThumbnails] = useState<any[]>([]);
    const [mediasPreviews, setMediasPreviews] = useState<number[]>(scheduledTask.payload.mediaFilesPreviews ?? []);

    const [tagCreators, setTagCreators] = useState<boolean>(scheduledTask.payload.taggedCreators?.length > 0 ? true : false);
    const [taggedCreators, setTaggedCreators] = useState<{ id: number; name: string }[]>(scheduledTask.payload.taggedCreators ?? []);
    const [taggedReleaseForms, setTaggedReleaseForms] = useState<{ id: number; name: string }[]>(
        scheduledTask.payload.taggedReleaseForms ?? [],
    );

    const [lockMessage, setLockMessage] = useState<boolean>(scheduledTask.payload.lockMessagePrice > 0 ? true : false);
    const [lockMessagePrice, setLockMessagePrice] = useState<number>(scheduledTask.payload.lockMessagePrice ?? 0);
    const [lockedText, setLockedText] = useState<boolean>(scheduledTask.payload.lockedText);

    const [autoUnsendTimer, setAutoUnsendTimer] = useState<boolean>(scheduledTask.payload.autoUnsendTimerPeriodAmount > 0 ? true : false);
    const autoUnsendTimerPeriod: string = 'hours';
    const [autoUnsendTimerPeriodAmount, setAutoUnsendTimerPeriodAmount] = useState<number>(
        scheduledTask.payload.autoUnsendTimerPeriodAmount ?? 4,
    );

    const [delayBetweenMessages, setDelayBetweenMessages] = useState<number>(scheduledTask.payload.delayBetweenMessages ?? 5000);

    const [message, setMessage] = useState<string>(scheduledTask.payload.message);
    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const [usernameReplacement, setUsernameReplacement] = useState<string>(scheduledTask.payload.usernameReplacement ?? '');
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');

    const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);

    useEffect(() => {
        if (subscriber) {
            if (isSextforceActive(subscriber)) {
                setUnitCost(0);
            } else {
                setUnitCost(
                    d2f(
                        targetUsers === 'onlineActive'
                            ? subscriber._config.messageOnlineUsers.prices.unitCost
                            : subscriber._config.messageUsers.prices.unitCost,
                    ),
                );
            }
        }
    }, [subscriber, targetUsers]);

    const {
        lists,
        hasMore: listsHasMore,
        isLoading: listsLoading,
        isFetching: listsFetching,
        refetch: refetchLists,
    } = useSubscriberLists(targetUsers === 'lists' || excludeUsersList ? true : false);

    const { updateScheduledMessage, isUpdatingScheduledMessage } = useMessageUsersScheduledSessions();

    const [repeatMessage, setRepeatMessage] = useState<boolean>(!!scheduledTask.repeatEveryAmount && !!scheduledTask.repeatEveryUnit);

    const [repeatEveryAmount, setRepeatEveryAmount] = useState<number>(scheduledTask.repeatEveryAmount ?? 5);
    const [repeatEveryUnit, setRepeatEveryUnit] = useState<string>(scheduledTask.repeatEveryUnit ?? 'hours');
    const [repeatEndDate, setRepeatEndDate] = useState<Date | null>(scheduledTask.endDate ?? null);
    const [colorId, setColorId] = useState<string | undefined>(scheduledTask.colorId);

    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditional();

    useEffect(() => {
        const getMedias = async () => {
            if (
                open &&
                (!scheduledTask.payload.type || scheduledTask.payload.type === 'manual') &&
                scheduledTask.payload.mediaFiles?.length > 0
            ) {
                const mediaReturn = await getManyVaultMediaFromDatabase(scheduledTask.payload.mediaFiles);

                if (mediaReturn) {
                    setMedias(mediaReturn);
                }
            }
        };
        getMedias();
    }, [open]);

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason && reason === 'backdropClick') {
            return;
        }

        onClose();
    };

    const handleSave = () => {
        const payload = {
            name: name.trim(),
            scheduleMessageDate: scheduleMessage ? scheduleMessageDate : undefined,
            targetUsers,
            targetListId: undefined,
            targetListName: undefined,
            targetLists,
            targetRecentSpendersDateFrom,
            targetRecentSpendersDateTo,
            targetRecentFansDateFrom,
            targetRecentFansDateTo,
            excludeCreators,
            excludeUnreadChats,
            excludeLimitMessageCount: excludeLimitMessage ? excludeLimitMessageCount : undefined,
            excludeLists: excludeUsersList ? excludeUsersLists : undefined,
            excludeSpentCondition: excludeUsersSpent ? excludeSpentCondition : undefined,
            excludeSpentAmount: excludeUsersSpent ? excludeSpentAmount : undefined,
            excludeSpentPeriod: excludeUsersSpentPeriod ? excludeSpentPeriod : undefined,
            excludeSpentPeriodAmount: excludeUsersSpentPeriod ? excludeSpentPeriodAmount : undefined,
            excludeMessagedInPeriod: excludeUsersMessagedIn ? excludeUsersMessagedInPeriod : undefined,
            excludeMessagedInPeriodAmount: excludeUsersMessagedIn ? excludeUsersMessagedInPeriodAmount : undefined,
            excludeFans: targetUsers === 'onlineFollowing' ? excludeFans : false,
            type: messageType !== 'manual' ? messageType : undefined,
            sourceAlbumId: messageType === 'album' ? sourceAlbumId : undefined,
            postImage: messageType === 'album' ? albumContentTypes.image : undefined,
            postVideo: messageType === 'album' ? albumContentTypes.video : undefined,
            postAudio: messageType === 'album' ? albumContentTypes.audio : undefined,
            postGif: messageType === 'album' ? albumContentTypes.gif : undefined,
            postCaption: messageType === 'album' ? albumUseRandomCaption : undefined,
            captionCategory: (messageType === 'album' && albumUseRandomCaption) || messageType === 'caption' ? captionCategory : undefined,
            templateIds: messageType === 'template' && templateTags.length === 0 ? templateIds : undefined,
            templateTags: messageType === 'template' && templateTags.length > 0 ? templateTags : undefined,
            templateTagsPaidOnly: messageType === 'template' && templateTags.length > 0 ? templateTagsPaidOnly : undefined,
            lockMessagePrice: messageType === 'manual' && lockMessage ? lockMessagePrice : undefined,
            lockedText: messageType === 'manual' && lockMessage && lockedText ? lockedText : undefined,
            autoUnsendTimerPeriod: autoUnsendTimer ? autoUnsendTimerPeriod : undefined,
            autoUnsendTimerPeriodAmount: autoUnsendTimer ? autoUnsendTimerPeriodAmount : undefined,
            message: messageType === 'manual' ? message : '',
            usernameReplacement: usernameReplacement.trim().length > 0 ? usernameReplacement.trim() : undefined,
            saveTemplateName:
                messageType === 'manual' && saveTemplate && saveTemplateName.trim().length > 0 ? saveTemplateName.trim() : undefined,
            medias: messageType === 'manual' ? medias.map(media => media.id) : [],
            mediasPreviews: messageType === 'manual' ? mediasPreviews : [],
            taggedCreators: messageType === 'manual' && tagCreators ? taggedCreators : undefined,
            taggedReleaseForms: messageType === 'manual' && taggedReleaseForms ? taggedReleaseForms : undefined,
            delayBetweenMessages: typeof delayBetweenMessages === 'number' ? delayBetweenMessages : undefined,
            colorId,
            repeatEveryAmount: repeatMessage ? repeatEveryAmount : undefined,
            repeatEveryUnit: repeatMessage ? repeatEveryUnit : undefined,
            repeatEndDate:
                repeatMessage && repeatEndDate && !moment(repeatEndDate).isSame('1970-01-01T00:00:00.000Z') ? repeatEndDate : undefined,
        };

        setIsSendingMessage(true);

        updateScheduledMessage(scheduledTask._id || '', payload)
            .then(result => {
                if (result) {
                    enqueueSnackbar('Scheduled message settings updated.', { variant: 'success', autoHideDuration: 3000 });

                    handleClose({}, 'escapeKeyDown');
                } else {
                    enqueueSnackbar('Failed to update scheduled message settings.', { variant: 'error', autoHideDuration: 3000 });
                }
            })
            .catch(() => {
                enqueueSnackbar('Failed to update scheduled message settings.', { variant: 'error', autoHideDuration: 3000 });
            })
            .finally(() => {
                setIsSendingMessage(false);
            });
    };

    const sendMessage = () => {};

    // Check the message doesn't contain any OnlyFans restricted words
    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (text && !isLoadingRestrictedWords && restrictedWords && subscriber) {
                // const lowerCaseMessage = text.toLowerCase();
                // const wordsInMessage = lowerCaseMessage.split(/\b\w+\b/g);

                // const restrictedWordsFound = wordsInMessage.filter(word => restrictedWords.has(word));

                // if (subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList?.includes(restrictedWordsFound)) {
                //     // Remove any words that are in the whitelist
                //     restrictedWordsFound.filter(word => !subscriber.sextforce?.bigBrother?.restrictedWordsWhiteList?.includes(word));
                // }
                const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);

                setMessageHasRestrictedWords(restrictedWordsFound.length > 0 ? true : false);
                setMessageFoundRestrictedWords(restrictedWordsFound);
            } else {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
            }
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );
    // Reset the whole form to default state
    const resetForm = () => {
        setMessageUsersCurrentSession(null);

        setName('');
        setScheduleMessage(false);
        setScheduleMessageDate(null);
        setColorId('');
        setRepeatMessage(false);
        setRepeatEveryAmount(1);
        setRepeatEveryUnit('hours');
        //setRepeatEndDate(null);

        setTargetUsers((localStorage.getItem('messageUsersTargetUsers') as OnlyFansSubscriptionsUserType) || 'onlineFollowing');
        setTargetRecentSpendersDateFrom(moment().subtract(1, 'months').startOf('day').toDate());
        setTargetRecentSpendersDateTo(moment().toDate());
        setTargetRecentFansDateFrom(moment().subtract(1, 'months').startOf('day').toDate());
        setTargetRecentFansDateTo(moment().toDate());

        setExcludeCreators(true);

        setExcludeUnreadChats(false);

        setExcludeLimitMessage(false);
        setExcludeLimitMessageCount(1);

        setExcludeUsersList(false);
        setExcludeUsersLists([]);

        setExcludeUsersSpent(false);
        setExcludeSpentCondition('eq');
        setExcludeSpentAmount(0);

        setExcludeUsersSpentPeriod(false);
        setExcludeSpentPeriod('months');
        setExcludeSpentPeriodAmount(1);

        setExcludeUsersMessagedIn(false);
        setExcludeUsersMessagedInPeriod('hours');
        setExcludeUsersMessagedInPeriodAmount(1);

        setMessageType('manual');
        setSourceAlbumId(0);
        setAlbumContentTypes({ image: true, video: true, audio: false, gif: false });
        setAlbumUseRandomCaption(false);
        setCaptionCategory('');
        setTemplateIds([]);

        setIncludeMedia(false);
        setMedias([]);
        setMediasPreviews([]);

        setTagCreators(false);
        setTaggedCreators([]);
        setTaggedReleaseForms([]);

        setLockMessage(false);
        setLockMessagePrice(0);
        setLockedText(false);

        setAutoUnsendTimer(false);
        setAutoUnsendTimerPeriodAmount(4);

        setDelayBetweenMessages(
            localStorage.getItem('messageUsersDelayBetweenMessages')
                ? parseInt(localStorage.getItem('messageUsersDelayBetweenMessages') as string)
                : 5000,
        );

        setUsernameReplacement('');
        setMessage('');
        setMessageHasRestrictedWords(false);
        setMessageFoundRestrictedWords([]);
        setSaveTemplate(false);
        setSaveTemplateName('');
    };

    const getAudio = (url: string) => getSubscriberAudio(subscriber, url);

    const getThumbnail = (thumbId: number, thumbUrl: string) => {
        getSubscriberThumbnail(subscriber!, thumbId, thumbUrl)?.then(blob => {
            if (blob) {
                setThumbnails(prevState => {
                    return [...prevState, { thumbId, blob }];
                });
            } else {
                setThumbnails(prevState => {
                    return [...prevState, { thumbId, blob: null }];
                });
            }
        });
    };

    const rawMessage = stripHtml(message);

    const isNotValid: boolean =
        (scheduleMessage &&
            (!name ||
                name.trim() === '' ||
                !scheduleMessageDate ||
                !moment(scheduleMessageDate).isValid() ||
                !moment(scheduleMessageDate).isAfter(moment()))) ||
        (repeatMessage && repeatEveryAmount === 0) ||
        (excludeUsersList && (!excludeUsersLists || excludeUsersLists.length === 0)) ||
        (excludeLimitMessage && excludeLimitMessageCount === 0) ||
        (excludeUsersSpentPeriod && excludeSpentPeriodAmount === 0) ||
        (messageType === 'manual' &&
            ((tagCreators && taggedCreators.length === 0 && taggedReleaseForms.length === 0) ||
                (lockMessage && lockMessagePrice < 3) ||
                (lockMessage && !lockedText && medias.length === 0) ||
                (rawMessage.trim().length === 0 && medias.length === 0 && lockMessage) ||
                (rawMessage.trim().length === 0 && !lockMessage && medias.length === 0) ||
                (messageHasRestrictedWords && isSextforceActive(subscriber) && subscriber.sextforce?.bigBrother?.active) ||
                (saveTemplate && (!saveTemplateName || saveTemplateName.trim().length === 0)) ||
                (includeMedia && (medias.length === 0 || medias.length > 40)))) ||
        (messageType === 'album' &&
            (sourceAlbumId <= 0 ||
                !(albumContentTypes.image || albumContentTypes.video || albumContentTypes.audio || albumContentTypes.gif))) ||
        (messageType === 'template' && templateIds.length === 0 && templateTags.length === 0) ||
        (autoUnsendTimer &&
            (messageType !== 'manual' || !lockMessage || (lockMessage && !lockMessagePrice)) &&
            (autoUnsendTimerPeriodAmount === 0 || autoUnsendTimerPeriodAmount > 24)) ||
        (targetUsers === 'lists' && targetLists.length === 0) ||
        (targetUsers === 'recentSpenders' && (!targetRecentSpendersDateFrom || !targetRecentSpendersDateTo)) ||
        isSendingMessage;

    return (
        <>
            <DialogAdaptive open={open} onClose={handleClose} disableEscapeKeyDown maxWidth="md" fullWidth>
                <StyledCardTitleBar title="Edit Message Settings" />
                <DialogContentAdaptive>
                    <MessageUsersForm
                        subscriber={subscriber}
                        loading={false}
                        session={messageUsersCurrentSession}
                        unitCost={unitCost}
                        lists={lists}
                        listsLoading={listsLoading || listsFetching || listsHasMore}
                        refetchLists={refetchLists}
                        name={name}
                        setName={setName}
                        scheduleMessage={scheduleMessage}
                        setScheduleMessage={setScheduleMessage}
                        scheduleMessageDate={scheduleMessageDate}
                        setScheduleMessageDate={setScheduleMessageDate}
                        colorId={colorId}
                        setColorId={setColorId}
                        repeatMessage={repeatMessage}
                        setRepeatMessage={setRepeatMessage}
                        repeatEveryAmount={repeatEveryAmount}
                        setRepeatEveryAmount={setRepeatEveryAmount}
                        repeatEveryUnit={repeatEveryUnit}
                        setRepeatEveryUnit={setRepeatEveryUnit}
                        repeatEndDate={repeatEndDate}
                        setRepeatEndDate={setRepeatEndDate}
                        targetUsers={targetUsers}
                        setTargetUsers={setTargetUsers}
                        targetLists={targetLists}
                        setTargetLists={setTargetLists}
                        targetRecentSpendersDateFrom={targetRecentSpendersDateFrom}
                        setTargetRecentSpendersDateFrom={setTargetRecentSpendersDateFrom}
                        targetRecentSpendersDateTo={targetRecentSpendersDateTo}
                        setTargetRecentSpendersDateTo={setTargetRecentSpendersDateTo}
                        targetRecentFansDateFrom={targetRecentFansDateFrom}
                        setTargetRecentFansDateFrom={setTargetRecentFansDateFrom}
                        targetRecentFansDateTo={targetRecentFansDateTo}
                        setTargetRecentFansDateTo={setTargetRecentFansDateTo}
                        excludeCreators={excludeCreators}
                        setExcludeCreators={setExcludeCreators}
                        excludeUnreadChats={excludeUnreadChats}
                        setExcludeUnreadChats={setExcludeUnreadChats}
                        excludeLimitMessage={excludeLimitMessage}
                        setExcludeLimitMessage={setExcludeLimitMessage}
                        excludeLimitMessageCount={excludeLimitMessageCount}
                        setExcludeLimitMessageCount={setExcludeLimitMessageCount}
                        excludeUsersList={excludeUsersList}
                        setExcludeUsersList={setExcludeUsersList}
                        excludedLists={excludeUsersLists}
                        setExcludedLists={setExcludeUsersLists}
                        excludeUsersSpent={excludeUsersSpent}
                        setExcludeUsersSpent={setExcludeUsersSpent}
                        excludeSpentCondition={excludeSpentCondition}
                        setExcludeSpentCondition={setExcludeSpentCondition}
                        excludeSpentAmount={excludeSpentAmount}
                        setExcludeSpentAmount={setExcludeSpentAmount}
                        excludeUsersSpentPeriod={excludeUsersSpentPeriod}
                        setExcludeUsersSpentPeriod={setExcludeUsersSpentPeriod}
                        excludeSpentPeriod={excludeSpentPeriod}
                        setExcludeSpentPeriod={setExcludeSpentPeriod}
                        excludeSpentPeriodAmount={excludeSpentPeriodAmount}
                        setExcludeSpentPeriodAmount={setExcludeSpentPeriodAmount}
                        excludeUsersMessagedIn={excludeUsersMessagedIn}
                        setExcludeUsersMessagedIn={setExcludeUsersMessagedIn}
                        excludeUsersMessagedInPeriod={excludeUsersMessagedInPeriod}
                        setExcludeUsersMessagedInPeriod={setExcludeUsersMessagedInPeriod}
                        excludeUsersMessagedInPeriodAmount={excludeUsersMessagedInPeriodAmount}
                        setExcludeUsersMessagedInPeriodAmount={setExcludeUsersMessagedInPeriodAmount}
                        excludeFans={excludeFans}
                        setExcludeFans={setExcludeFans}
                        messageType={messageType}
                        setMessageType={setMessageType}
                        sourceAlbumId={sourceAlbumId}
                        setSourceAlbumId={setSourceAlbumId}
                        albumContentTypes={albumContentTypes}
                        setAlbumContentTypes={setAlbumContentTypes}
                        albumUseRandomCaption={albumUseRandomCaption}
                        setAlbumUseRandomCaption={setAlbumUseRandomCaption}
                        captionCategory={captionCategory}
                        setCaptionCategory={setCaptionCategory}
                        templateIds={templateIds}
                        templateTags={templateTags}
                        setTemplateTags={setTemplateTags}
                        templateTagsPaidOnly={templateTagsPaidOnly}
                        setTemplateTagsPaidOnly={setTemplateTagsPaidOnly}
                        setTemplateIds={setTemplateIds}
                        includeMedia={includeMedia}
                        setIncludeMedia={setIncludeMedia}
                        medias={medias}
                        setMedias={setMedias}
                        thumbnails={thumbnails}
                        mediasPreviews={mediasPreviews}
                        setMediasPreviews={setMediasPreviews}
                        setThumbnails={setThumbnails}
                        getThumbnail={getThumbnail}
                        getAudio={getAudio}
                        tagCreators={tagCreators}
                        setTagCreators={setTagCreators}
                        taggedCreators={taggedCreators}
                        setTaggedCreators={setTaggedCreators}
                        taggedReleaseForms={taggedReleaseForms}
                        setTaggedReleaseForms={setTaggedReleaseForms}
                        lockMessage={lockMessage}
                        setLockMessage={setLockMessage}
                        lockMessagePrice={lockMessagePrice}
                        setLockMessagePrice={setLockMessagePrice}
                        lockedText={lockedText}
                        setLockedText={setLockedText}
                        autoUnsendTimer={autoUnsendTimer}
                        setAutoUnsendTimer={setAutoUnsendTimer}
                        autoUnsendTimerPeriodAmount={autoUnsendTimerPeriodAmount}
                        setAutoUnsendTimerPeriodAmount={setAutoUnsendTimerPeriodAmount}
                        delayBetweenMessages={delayBetweenMessages}
                        setDelayBetweenMessages={setDelayBetweenMessages}
                        message={message}
                        setMessage={setMessage}
                        messageHasRestrictedWords={messageHasRestrictedWords}
                        messageFoundRestrictedWords={messageFoundRestrictedWords}
                        checkForRestrictedWords={checkForRestrictedWords}
                        usernameReplacement={usernameReplacement}
                        setUsernameReplacement={setUsernameReplacement}
                        saveTemplate={saveTemplate}
                        setSaveTemplate={setSaveTemplate}
                        saveTemplateName={saveTemplateName}
                        setSaveTemplateName={setSaveTemplateName}
                        isSendingMessage={isSendingMessage}
                        sendMessage={sendMessage}
                        resetForm={resetForm}
                        theme={theme}
                        edit={true}
                        isInsideDialog={true}
                    />
                </DialogContentAdaptive>
                <StyledDialogActions
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        color="error"
                        size="medium"
                        disabled={isUpdatingScheduledMessage}
                        onClick={() => {
                            handleClose({}, 'escapeKeyDown');
                        }}
                    >
                        Close
                    </Button>
                    <LoadingButton
                        color="secondary"
                        variant="contained"
                        size="medium"
                        loading={isUpdatingScheduledMessage}
                        disabled={isNotValid}
                        onClick={() => {
                            handleSave();
                        }}
                    >
                        <span>Save Settings</span>
                    </LoadingButton>
                </StyledDialogActions>
            </DialogAdaptive>
        </>
    );
};

export default MessageUsersScheduledTaskChangeSettingsDialog;
